import React, { useCallback, useContext, useMemo } from 'react';
import { browserHistory } from 'react-router';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';
import MuiTableCell, { TableCellProps as MuiTableCellProps } from '@material-ui/core/TableCell';
import classnames from 'classnames';

import { TableContext } from './TableContext';

const styles = ({
  palette: {
    background,
    common: { white },
  },
  zIndex: { appBar },
  custom: {
    palette: { colorSupportPowder },
  },
}: Theme) =>
  createStyles({
    root: {
      borderBottomColor: background.default,
    },
    variantEmptyState: {
      border: 'none',
    },
    noWrap: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      maxWidth: 172,
    },
    href: {
      '&:hover': {
        cursor: 'pointer',
        textDecoration: 'underline',
      },
    },
    colorSupportPowder: {
      border: `1px solid ${colorSupportPowder}`,
    },
    /* Styles applied to the root element if `context.table.stickyHeader={true}`. */
    stickyHeader: {
      position: 'sticky',
      top: 0,
      left: 0,
      backgroundColor: white,
      zIndex: appBar - 1,
    },
  });

export interface TableCellProps extends Omit<MuiTableCellProps, 'classes' | 'variant'>, WithStyles<typeof styles> {
  variant?: MuiTableCellProps['variant'] | 'emptyState';
  noWrap?: boolean;
  href?: string;
  color?: 'supportPowder';
}

const TableCellComponent: React.FC<TableCellProps> = ({
  classes,
  className,
  children,
  variant: initialVariant,
  noWrap,
  href,
  color,
  ...rest
}) => {
  const table = useContext(TableContext);

  const variant = useMemo(() => {
    if (initialVariant === 'emptyState') {
      return undefined;
    }
    return initialVariant;
  }, [initialVariant]);

  const handleClick = useCallback(() => {
    if (href) {
      browserHistory.push(href);
    }
  }, [href]);

  return (
    <MuiTableCell
      className={classnames(classes.root, className, {
        [classes.variantEmptyState]: initialVariant === 'emptyState',
        [classes.noWrap]: noWrap,
        [classes.href]: href,
        [classes.stickyHeader]: variant === 'head' && table && table.stickyHeader,
        [classes.colorSupportPowder]: color === 'supportPowder',
      })}
      variant={variant}
      onClick={handleClick}
      {...rest}
    >
      {children || '—'}
    </MuiTableCell>
  );
};

export const TableCell = withStyles(styles)(TableCellComponent);
